import React, { FormEvent, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarCheck, Check, Clock, FileText, History, X } from 'lucide-react'
import { format } from 'date-fns'
import { leavesApi } from '../api/leaves'
import { Avatar } from '../components/common/Avatar'
import { Badge } from '../components/common/Badge'
import { Modal } from '../components/common/Modal'
import { useAuthStore } from '../store/authStore'
import { hasFullAccess } from '../lib/roles'
import type { LeaveRequest, LeaveRequestType, LeaveStatus } from '../types'

const statusColors: Record<LeaveStatus, string> = {
  PENDING: '#f59e0b',
  APPROVED: '#16a34a',
  REJECTED: '#ef4444',
  CANCELLED: '#64748b',
}

const requestTypeColors = {
  LEAVE: '#0f766e',
  TIME_OFF: '#2563eb',
} as const

function requestTypeLabel(leave: LeaveRequest) {
  return leave.requestType === 'TIME_OFF' ? 'Time off' : 'Leave'
}

function hoursLabel(hours: number) {
  if (!Number.isFinite(hours) || Math.abs(hours) < 0.01) return '0h'
  const whole = Math.floor(hours)
  const minutes = Math.round((hours - whole) * 60)
  return minutes > 0 ? `${whole}h ${minutes}m` : `${whole}h`
}

function durationLabel(leave: LeaveRequest) {
  if (leave.requestType === 'TIME_OFF') {
    return `${hoursLabel(leave.totalHours)}${leave.remainingHours > 0 ? ` · ${hoursLabel(leave.remainingHours)} pending` : ''}`
  }
  return `${leave.totalDays} day${leave.totalDays === 1 ? '' : 's'}`
}

const actionLabels: Record<string, string> = {
  CREATED: 'Created request',
  UPDATED: 'Updated request',
  APPROVED: 'Approved leave',
  REJECTED: 'Rejected leave',
  CANCELLED_BY_EMPLOYEE: 'Cancelled by employee',
  CANCELLED_BY_MANAGER: 'Cancelled by manager',
  CANCELLED_BY_ADMIN: 'Cancelled by admin',
  COMPENSATION_ADDED: 'Compensation added',
}

function todayInputValue() {
  return format(new Date(), 'yyyy-MM-dd')
}

function formatDate(value: string) {
  return format(new Date(value), 'dd MMM yyyy')
}

function formatDateTime(value: string) {
  return format(new Date(value), 'dd MMM yyyy, h:mm a')
}

function statusLabel(status: LeaveStatus) {
  return status.charAt(0) + status.slice(1).toLowerCase()
}

function leaveDates(leave: LeaveRequest) {
  if (leave.requestType === 'TIME_OFF') {
    return `${formatDate(leave.startDate)} · ${leave.startTime || '-'} - ${leave.endTime || '-'}`
  }
  const start = formatDate(leave.startDate)
  const end = formatDate(leave.endDate)
  return start === end ? start : `${start} - ${end}`
}

function isWithinNext15Days(leave: LeaveRequest) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const until = new Date(today)
  until.setDate(until.getDate() + 14)
  return new Date(leave.startDate) <= until && new Date(leave.endDate) >= today
}

function next15Days() {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Array.from({ length: 15 }, (_, index) => {
    const date = new Date(today)
    date.setDate(today.getDate() + index)
    return date
  })
}

function leaveCoversDay(leave: LeaveRequest, day: Date) {
  const start = new Date(leave.startDate)
  const end = new Date(leave.endDate)
  start.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)
  return start <= day && end >= day
}

function timeMinutes(value?: string | null): number | null {
  if (!value) return null
  const [hours, minutes] = value.split(':').map(Number)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null
  return hours * 60 + minutes
}

function apiErrorMessage(error: unknown): string {
  return (error as { response?: { data?: { error?: string } } })?.response?.data?.error
    || (error as Error)?.message
    || ''
}

function leaveHoverText(leave: LeaveRequest) {
  return [
    leave.employee.fullName,
    `Dates: ${leaveDates(leave)}`,
    `Type: ${requestTypeLabel(leave)}`,
    `Duration: ${durationLabel(leave)}`,
    `Status: ${statusLabel(leave.status)}`,
    `Reason: ${leave.reason || '-'}`,
    `Applied: ${formatDateTime(leave.createdAt)}`,
    leave.approvedBy ? `Approved by: ${leave.approvedBy.fullName}` : null,
    leave.approvedAt ? `Approved at: ${formatDateTime(leave.approvedAt)}` : null,
    leave.rejectedAt ? `Rejected at: ${formatDateTime(leave.rejectedAt)}` : null,
    leave.cancelledAt ? `Cancelled at: ${formatDateTime(leave.cancelledAt)}` : null,
    leave.cancellationReason ? `Cancel reason: ${leave.cancellationReason}` : null,
  ].filter(Boolean).join('\n')
}

function emptyForm() {
  const today = todayInputValue()
  return {
    requestType: 'LEAVE' as LeaveRequestType,
    startDate: today,
    endDate: today,
    startTime: '09:00',
    endTime: '12:00',
    leaveType: 'Casual',
    reason: '',
  }
}

export default function LeaveManagementPage() {
  const { user } = useAuthStore()
  const canManageLeave = hasFullAccess(user?.role)
  const qc = useQueryClient()
  const [applyOpen, setApplyOpen] = useState(false)
  const [confirmedReminder, setConfirmedReminder] = useState(false)
  const [reminderChecked, setReminderChecked] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [selectedLeave, setSelectedLeave] = useState<LeaveRequest | null>(null)
  const [statusFilter, setStatusFilter] = useState<'ALL' | LeaveStatus>('ALL')
  const [formError, setFormError] = useState('')
  const [reportMonth, setReportMonth] = useState(format(new Date(), 'yyyy-MM'))
  const [reportOpen, setReportOpen] = useState(false)
  const [compensationForm, setCompensationForm] = useState({
    compensationDate: todayInputValue(),
    startTime: '18:00',
    endTime: '19:00',
    note: '',
  })

  const leavesQuery = useQuery({
    queryKey: ['leaves', statusFilter],
    queryFn: () =>
      leavesApi
        .listLeaves(statusFilter === 'ALL' ? undefined : { status: statusFilter })
        .then((res) => res.data.data),
  })

  const upcomingQuery = useQuery({
    queryKey: ['leaves', 'upcoming', 'all-employees'],
    queryFn: () => leavesApi.getUpcoming().then((res) => res.data.data),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })

  const pendingQuery = useQuery({
    queryKey: ['leaves', 'pending-approvals'],
    queryFn: () => leavesApi.listLeaves({ status: 'PENDING' }).then((res) => res.data.data),
    enabled: canManageLeave,
  })

  const selectedLeaveQuery = useQuery({
    queryKey: ['leaves', selectedLeave?.id],
    queryFn: () => leavesApi.getLeave(selectedLeave!.id).then((res) => res.data.data),
    enabled: !!selectedLeave?.id,
  })

  const reportQuery = useQuery({
    queryKey: ['leaves', 'monthly-report', reportMonth],
    queryFn: () => leavesApi.monthlyReport({ month: reportMonth }).then((res) => res.data.data),
    enabled: canManageLeave && reportOpen,
  })

  const invalidateLeaves = () => {
    qc.invalidateQueries({ queryKey: ['leaves'] })
  }

  const applyMutation = useMutation({
    mutationFn: () =>
      leavesApi.applyLeave({
        ...form,
        verbalApprovalConfirmed: confirmedReminder,
        endDate: form.requestType === 'TIME_OFF' ? form.startDate : form.endDate,
        startTime: form.requestType === 'TIME_OFF' ? form.startTime : null,
        endTime: form.requestType === 'TIME_OFF' ? form.endTime : null,
        leaveType: form.leaveType || null,
        reason: form.reason || null,
      }),
    onSuccess: () => {
      setForm(emptyForm())
      setConfirmedReminder(false)
      setApplyOpen(false)
      invalidateLeaves()
    },
  })

  const approveMutation = useMutation({
    mutationFn: (leaveId: string) => leavesApi.approveLeave(leaveId),
    onSuccess: invalidateLeaves,
  })

  const rejectMutation = useMutation({
    mutationFn: ({ leaveId, reason }: { leaveId: string; reason?: string }) =>
      leavesApi.rejectLeave(leaveId, reason),
    onSuccess: invalidateLeaves,
  })

  const cancelMutation = useMutation({
    mutationFn: ({ leaveId, reason }: { leaveId: string; reason?: string }) =>
      leavesApi.cancelLeave(leaveId, reason),
    onSuccess: invalidateLeaves,
  })

  const compensationMutation = useMutation({
    mutationFn: (leaveId: string) =>
      leavesApi.addCompensation(leaveId, {
        compensationDate: compensationForm.compensationDate,
        startTime: compensationForm.startTime,
        endTime: compensationForm.endTime,
        note: compensationForm.note || null,
      }),
    onSuccess: (res) => {
      setSelectedLeave(res.data.data)
      setCompensationForm({ compensationDate: todayInputValue(), startTime: '18:00', endTime: '19:00', note: '' })
      invalidateLeaves()
    },
  })

  const leaves = leavesQuery.data ?? []
  const upcoming = upcomingQuery.data ?? []
  const pending = pendingQuery.data ?? []
  const next15DayLeaves = canManageLeave
    ? leaves.filter(isWithinNext15Days)
    : upcoming.filter((leave) => leave.requestType === 'LEAVE' && isWithinNext15Days(leave))

  const mySummary = useMemo(() => {
    return leaves.reduce(
      (acc, leave) => {
        acc[leave.status] += 1
        return acc
      },
      { PENDING: 0, APPROVED: 0, REJECTED: 0, CANCELLED: 0 } as Record<LeaveStatus, number>,
    )
  }, [leaves])

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setFormError('')
    if (form.requestType === 'TIME_OFF') {
      const start = timeMinutes(form.startTime)
      const end = timeMinutes(form.endTime)
      if (start === null || end === null || end <= start) {
        setFormError('End time must be after start time. For noon, choose 12:00 PM; 12:00 AM is midnight.')
        return
      }
    }
    applyMutation.mutate()
  }

  function handleReject(leaveId: string) {
    const reason = window.prompt('Reject reason or note')
    if (reason === null) return
    rejectMutation.mutate({ leaveId, reason })
  }

  function handleCancel(leave: LeaveRequest) {
    const needsReason = canManageLeave && leave.status === 'APPROVED'
    const reason = window.prompt(needsReason ? 'Cancellation reason is required' : 'Cancellation note')
    if (reason === null) return
    cancelMutation.mutate({ leaveId: leave.id, reason })
  }

  function handleApprove(leaveId: string) {
    approveMutation.mutate(leaveId)
  }

  return (
    <div style={{ padding: 24, color: '#0f172a' }}>
      <div style={{ maxWidth: 1320, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ color: '#0f766e', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', marginBottom: 8 }}>
              Leave Management
            </div>
            <h1 style={{ margin: 0, fontSize: 28, letterSpacing: 0 }}>Apply and track leave</h1>
            <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: 14 }}>
              Submit leave requests, review approvals, and keep a complete activity history.
            </p>
          </div>
          {canManageLeave && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button type="button" style={smallButtonStyle} onClick={() => setReportOpen(true)}>
                Monthly report
              </button>
              {Object.entries(mySummary).map(([status, count]) => (
                <div key={status} style={summaryCardStyle}>
                  <span style={{ color: '#64748b', fontSize: 11, fontWeight: 700 }}>{statusLabel(status as LeaveStatus)}</span>
                  <strong style={{ fontSize: 20, color: statusColors[status as LeaveStatus] }}>{count}</strong>
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          className="leave-main-grid"
          style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 420px) 1fr', gap: 16, alignItems: 'start' }}
        >
          <section style={panelStyle}>
            <div style={sectionTitleStyle}>
              <CalendarCheck size={18} />
              <span>Apply Leave</span>
            </div>
            <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: 13, lineHeight: 1.5 }}>
              Start a new leave request. Your manager will approve, reject, or cancel it from their view.
            </p>
            <button
              type="button"
            onClick={() => {
              setConfirmedReminder(false)
              setReminderChecked(false)
              setFormError('')
              setForm(emptyForm())
              setApplyOpen(true)
            }}
              style={{ ...primaryButtonStyle, width: '100%' }}
            >
              <CalendarCheck size={16} />
              Apply Leave
            </button>
          </section>

          {canManageLeave && (
            <div style={{ display: 'grid', gap: 16 }}>
              <section style={panelStyle}>
                <div style={sectionTitleStyle}>
                  <Clock size={18} />
                  <span>Manager Calendar - Next 15 Days</span>
                </div>
                <ManagerLeaveCalendar leaves={next15DayLeaves} onOpen={setSelectedLeave} />
              </section>

              <section style={panelStyle}>
                <div style={sectionTitleStyle}>
                  <FileText size={18} />
                  <span>Approval Requests</span>
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {pending.length === 0 && <div style={emptyStyle}>No pending leave approvals.</div>}
                  {pending.map((leave) => (
                    <LeaveCompactRow
                      key={leave.id}
                      leave={leave}
                      onOpen={() => setSelectedLeave(leave)}
                      actions={
                        <>
                          <button style={smallButtonStyle} onClick={() => handleApprove(leave.id)}>Approve</button>
                          <button style={dangerButtonStyle} onClick={() => handleReject(leave.id)}>Reject</button>
                        </>
                      }
                    />
                  ))}
                </div>
              </section>
            </div>
          )}
          {!canManageLeave && (
            <section style={panelStyle}>
              <div style={sectionTitleStyle}>
                <Clock size={18} />
                <span>Team Calendar - Next 15 Days</span>
              </div>
              <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: 13, lineHeight: 1.5 }}>
                Check who is already on leave or time off before applying.
              </p>
              <ManagerLeaveCalendar leaves={next15DayLeaves} readonly />
            </section>
          )}
        </div>

        <section style={{ ...panelStyle, marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 12 }}>
            <div style={sectionTitleStyle}>
              <History size={18} />
              <span>Leave History</span>
            </div>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as 'ALL' | LeaveStatus)}
              style={{ ...inputStyle, width: 150 }}
            >
              <option value="ALL">All status</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
              <thead>
                <tr style={{ background: '#f8fafc', color: '#64748b', fontSize: 12, textTransform: 'uppercase' }}>
                  <th style={thStyle}>Employee</th>
                  <th style={thStyle}>Dates</th>
                  <th style={thStyle}>Duration</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leaves.map((leave) => (
                  <tr key={leave.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar user={leave.employee} size="sm" />
                        <strong>{leave.employee.fullName}</strong>
                      </div>
                    </td>
                    <td style={tdStyle}>{leaveDates(leave)}</td>
                    <td style={tdStyle}>{durationLabel(leave)}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'grid', gap: 3 }}>
                        <span style={{ color: requestTypeColors[leave.requestType], fontWeight: 800 }}>
                          {requestTypeLabel(leave)}
                        </span>
                        <span style={{ color: '#64748b', fontSize: 12 }}>{leave.leaveType || '-'}</span>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <Badge color={statusColors[leave.status]} label={statusLabel(leave.status)} size="sm" />
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button style={smallButtonStyle} onClick={() => setSelectedLeave(leave)}>Details</button>
                        {leave.status === 'PENDING' && !canManageLeave && leave.employeeId === user?.id && (
                          <button style={dangerButtonStyle} onClick={() => handleCancel(leave)}>Cancel</button>
                        )}
                        {canManageLeave && leave.status === 'APPROVED' && (
                          <button style={dangerButtonStyle} onClick={() => handleCancel(leave)}>Cancel</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {leaves.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ ...tdStyle, color: '#64748b', textAlign: 'center', padding: 28 }}>
                      No leave requests found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <Modal
        isOpen={!!selectedLeave}
        onClose={() => setSelectedLeave(null)}
        title="Leave Details"
        width={760}
      >
        {selectedLeaveQuery.data && (
          <LeaveDetails
            leave={selectedLeaveQuery.data}
            canManageLeave={canManageLeave}
            currentUserId={user?.id}
            onApprove={handleApprove}
            onReject={handleReject}
            onCancel={handleCancel}
            onAddCompensation={(leaveId) => compensationMutation.mutate(leaveId)}
            compensationForm={compensationForm}
            setCompensationForm={setCompensationForm}
            isSubmitting={approveMutation.isPending || rejectMutation.isPending || cancelMutation.isPending || compensationMutation.isPending}
            isCompensating={compensationMutation.isPending}
          />
        )}
      </Modal>

      <Modal isOpen={reportOpen} onClose={() => setReportOpen(false)} title="Monthly Leave and Time Off Report" width={980}>
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={fieldStyle}>
              Month
              <input type="month" value={reportMonth} onChange={(event) => setReportMonth(event.target.value)} style={inputStyle} />
            </label>
            <a
              href={leavesApi.monthlyReportExportUrl({ month: reportMonth })}
              target="_blank"
              rel="noreferrer"
              style={{ ...primaryButtonStyle, height: 38, padding: '0 14px', textDecoration: 'none' }}
            >
              Export CSV
            </a>
          </div>
          {reportQuery.isLoading ? (
            <div style={emptyStyle}>Loading report...</div>
          ) : reportQuery.data ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))', gap: 8 }}>
                <div style={summaryCardStyle}><span>Leave days</span><strong>{reportQuery.data.summary.totalLeaveDays}</strong></div>
                <div style={summaryCardStyle}><span>Time off</span><strong>{reportQuery.data.summary.totalTimeOffHours}h</strong></div>
                <div style={summaryCardStyle}><span>Compensated</span><strong>{reportQuery.data.summary.totalCompensatedHours}h</strong></div>
                <div style={summaryCardStyle}><span>Remaining</span><strong>{reportQuery.data.summary.totalRemainingHours}h</strong></div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', color: '#64748b', fontSize: 12, textTransform: 'uppercase' }}>
                      <th style={thStyle}>Employee</th>
                      <th style={thStyle}>Type</th>
                      <th style={thStyle}>Dates / Time</th>
                      <th style={thStyle}>Leave</th>
                      <th style={thStyle}>Time off</th>
                      <th style={thStyle}>Comp</th>
                      <th style={thStyle}>Remain</th>
                      <th style={thStyle}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportQuery.data.requests.map((leave) => (
                      <tr key={leave.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                        <td style={tdStyle}>{leave.employee.fullName}</td>
                        <td style={tdStyle}>{leave.requestType === 'TIME_OFF' ? 'Time off' : 'Leave'}</td>
                        <td style={tdStyle}>{leaveDates(leave)}</td>
                        <td style={tdStyle}>{leave.requestType === 'LEAVE' ? leave.totalDays || '-' : '-'}</td>
                        <td style={tdStyle}>{leave.totalHours ? `${leave.totalHours}h` : '-'}</td>
                        <td style={tdStyle}>{leave.compensatedHours ? `${leave.compensatedHours}h` : '-'}</td>
                        <td style={tdStyle}>{leave.remainingHours ? `${leave.remainingHours}h` : '-'}</td>
                        <td style={tdStyle}><Badge color={statusColors[leave.status]} label={statusLabel(leave.status)} size="sm" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div style={emptyStyle}>Choose a month to generate the report.</div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={applyOpen}
        onClose={() => {
          setApplyOpen(false)
          setConfirmedReminder(false)
          setReminderChecked(false)
          setFormError('')
        }}
        title={confirmedReminder ? 'Apply Leave' : 'Leave Confirmation'}
        width={620}
      >
        {!confirmedReminder ? (
          <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ padding: 14, border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 8, color: '#92400e', lineHeight: 1.5, fontSize: 14 }}>
              Please apply for leave only after discussing with your Manager and receiving verbal approval before starting this application.
            </div>
            <label style={confirmCheckStyle}>
              <input
                type="checkbox"
                checked={reminderChecked}
                onChange={(event) => setReminderChecked(event.target.checked)}
              />
              <span>I have manually checked with my Manager and confirm I can continue.</span>
            </label>
            <button
              type="button"
              disabled={!reminderChecked}
              onClick={() => setConfirmedReminder(true)}
              style={{ ...primaryButtonStyle, opacity: reminderChecked ? 1 : 0.55, cursor: reminderChecked ? 'pointer' : 'not-allowed' }}
            >
              <Check size={16} />
              Continue to leave form
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 12 }}>
            <label style={fieldStyle}>
              Request type
              <select
                value={form.requestType}
                onChange={(event) => setForm((current) => ({ ...current, requestType: event.target.value as LeaveRequestType }))}
                style={inputStyle}
              >
                <option value="LEAVE">Leave</option>
                <option value="TIME_OFF">Time off</option>
              </select>
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={fieldStyle}>
                {form.requestType === 'TIME_OFF' ? 'Date' : 'Start date'}
                <input
                  required
                  type="date"
                  value={form.startDate}
                  onChange={(event) => setForm((current) => ({ ...current, startDate: event.target.value }))}
                  style={inputStyle}
                />
              </label>
              {form.requestType === 'LEAVE' ? (
                <label style={fieldStyle}>
                  End date
                  <input
                    required
                    type="date"
                    value={form.endDate}
                    onChange={(event) => setForm((current) => ({ ...current, endDate: event.target.value }))}
                    style={inputStyle}
                  />
                </label>
              ) : (
                <label style={fieldStyle}>
                  Total time
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <input
                      required
                      type="time"
                      value={form.startTime}
                      onChange={(event) => setForm((current) => ({ ...current, startTime: event.target.value }))}
                      style={inputStyle}
                    />
                    <input
                      required
                      type="time"
                      value={form.endTime}
                      onChange={(event) => setForm((current) => ({ ...current, endTime: event.target.value }))}
                      style={inputStyle}
                    />
                  </div>
                </label>
              )}
            </div>
            <label style={fieldStyle}>
              Label
              <select
                value={form.leaveType}
                onChange={(event) => setForm((current) => ({ ...current, leaveType: event.target.value }))}
                style={inputStyle}
              >
                <option value="Casual">Casual</option>
                <option value="Medical">Medical</option>
                <option value="Personal">Personal</option>
                <option value="Time off">Time off</option>
              </select>
            </label>
            <label style={fieldStyle}>
              Reason
              <textarea
                value={form.reason}
                onChange={(event) => setForm((current) => ({ ...current, reason: event.target.value }))}
                rows={6}
                style={{ ...inputStyle, minHeight: 150, paddingTop: 10, lineHeight: 1.45, resize: 'vertical' }}
              />
            </label>
            {formError && (
              <div style={errorStyle}>{formError}</div>
            )}
            {applyMutation.error && (
              <div style={errorStyle}>{apiErrorMessage(applyMutation.error) || 'Could not submit leave request.'}</div>
            )}
            <button type="submit" disabled={applyMutation.isPending} style={primaryButtonStyle}>
              <Check size={16} />
              Submit leave request
            </button>
          </form>
        )}
      </Modal>

      <style>{`
        @media (max-width: 1180px) {
          .leave-calendar-grid { grid-template-columns: repeat(4, minmax(112px, 1fr)) !important; }
        }
        @media (max-width: 980px) {
          .leave-main-grid { grid-template-columns: 1fr !important; }
          .leave-row-actions { justify-content: flex-start; }
          .leave-calendar-grid { grid-template-columns: repeat(2, minmax(112px, 1fr)) !important; }
        }
      `}</style>
    </div>
  )
}

function LeaveCompactRow({ leave, onOpen, actions }: { leave: LeaveRequest; onOpen: () => void; actions?: React.ReactNode }) {
  return (
    <div style={compactRowStyle} title={leaveHoverText(leave)}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
        <Avatar user={leave.employee} size="sm" />
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13 }}>{leave.employee.fullName}</strong>
            <Badge color={statusColors[leave.status]} label={statusLabel(leave.status)} size="sm" />
          </div>
          <div style={{ color: '#64748b', fontSize: 12, marginTop: 3 }}>
            {leaveDates(leave)} - {durationLabel(leave)}
          </div>
        </div>
      </div>
      <div className="leave-row-actions" style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {actions}
        <button style={smallButtonStyle} onClick={onOpen}>Open</button>
      </div>
    </div>
  )
}

function ManagerLeaveCalendar({
  leaves,
  onOpen,
  readonly = false,
}: {
  leaves: LeaveRequest[]
  onOpen?: (leave: LeaveRequest) => void
  readonly?: boolean
}) {
  const days = next15Days()

  return (
    <div className="leave-calendar-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(112px, 1fr))', gap: 8 }}>
      {days.map((day) => {
        const dayLeaves = leaves.filter((leave) => leaveCoversDay(leave, day))
        return (
          <div key={day.toISOString()} style={calendarDayStyle}>
            <div style={{ color: '#475569', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', marginBottom: 8 }}>
              {format(day, 'EEE dd')}
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              {dayLeaves.length === 0 && <span style={{ color: '#cbd5e1', fontSize: 12 }}>-</span>}
              {dayLeaves.map((leave) => (
                <button
                  key={`${day.toISOString()}-${leave.id}`}
                  title={readonly ? leave.employee.fullName : leaveHoverText(leave)}
                  onClick={() => {
                    if (!readonly && onOpen) onOpen(leave)
                  }}
                  style={{
                    border: `1px solid ${requestTypeColors[leave.requestType]}`,
                    borderLeft: `4px solid ${requestTypeColors[leave.requestType]}`,
                    background: '#ffffff',
                    borderRadius: 6,
                    padding: '6px 7px',
                    color: '#0f172a',
                    textAlign: 'left',
                    cursor: readonly ? 'default' : 'pointer',
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {leave.employee.fullName}
                  </div>
                  {!readonly && (
                    <div style={{ color: statusColors[leave.status], fontSize: 10, fontWeight: 800, marginTop: 2 }}>
                      {requestTypeLabel(leave)} · {statusLabel(leave.status)}
                    </div>
                  )}
                  {!readonly && leave.requestType === 'TIME_OFF' && leave.remainingHours > 0 && (
                    <div style={{ color: '#2563eb', fontSize: 10, fontWeight: 800, marginTop: 2 }}>
                      {hoursLabel(leave.remainingHours)} pending
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function LeaveDetails({
  leave,
  canManageLeave,
  currentUserId,
  onApprove,
  onReject,
  onCancel,
  onAddCompensation,
  compensationForm,
  setCompensationForm,
  isSubmitting,
  isCompensating,
}: {
  leave: LeaveRequest
  canManageLeave: boolean
  currentUserId?: string
  onApprove: (leaveId: string) => void
  onReject: (leaveId: string) => void
  onCancel: (leave: LeaveRequest) => void
  onAddCompensation: (leaveId: string) => void
  compensationForm: { compensationDate: string; startTime: string; endTime: string; note: string }
  setCompensationForm: React.Dispatch<React.SetStateAction<{ compensationDate: string; startTime: string; endTime: string; note: string }>>
  isSubmitting: boolean
  isCompensating: boolean
}) {
  const canApproveOrReject = canManageLeave && leave.status === 'PENDING' && leave.employeeId !== currentUserId
  const canCancel = canManageLeave
    ? leave.status === 'APPROVED'
    : leave.status === 'PENDING' && leave.employeeId === currentUserId

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
        <DetailItem label="Employee" value={leave.employee.fullName} />
        <DetailItem label="Type" value={leave.requestType === 'TIME_OFF' ? 'Time off' : 'Leave'} />
        <DetailItem label="Dates" value={leaveDates(leave)} />
        <DetailItem label="Duration" value={durationLabel(leave)} />
        <DetailItem label="Label" value={leave.leaveType || '-'} />
        <DetailItem label="Status" value={statusLabel(leave.status)} />
        {leave.requestType === 'TIME_OFF' && (
          <>
            <DetailItem label="Requested time" value={`${leave.startTime || '-'} - ${leave.endTime || '-'}`} />
            <DetailItem label="Compensation" value={`${hoursLabel(leave.compensatedHours)} done, ${hoursLabel(leave.remainingHours)} pending`} />
          </>
        )}
      </div>
      {leave.reason && (
        <div style={{ padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          <div style={{ color: '#64748b', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', marginBottom: 5 }}>Reason</div>
          <div style={{ fontSize: 14, lineHeight: 1.5 }}>{leave.reason}</div>
        </div>
      )}
      {leave.cancellationReason && (
        <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
          <div style={{ color: '#991b1b', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', marginBottom: 5 }}>Cancellation reason</div>
          <div style={{ fontSize: 14, lineHeight: 1.5 }}>{leave.cancellationReason}</div>
        </div>
      )}
      {(canApproveOrReject || canCancel) && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {canApproveOrReject && (
            <>
              <button disabled={isSubmitting} style={smallButtonStyle} onClick={() => onApprove(leave.id)}>Approve</button>
              <button disabled={isSubmitting} style={dangerButtonStyle} onClick={() => onReject(leave.id)}>Reject</button>
            </>
          )}
          {canCancel && (
            <button disabled={isSubmitting} style={dangerButtonStyle} onClick={() => onCancel(leave)}>Cancel leave</button>
          )}
        </div>
      )}
      {canManageLeave && leave.requestType === 'TIME_OFF' && leave.status === 'APPROVED' && (
        <div style={{ padding: 12, border: '1px solid #dbe4ef', borderRadius: 8, background: '#f8fafc' }}>
          <div style={sectionTitleStyle}>Add Compensation</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
            <label style={fieldStyle}>
              Date
              <input type="date" value={compensationForm.compensationDate} onChange={(event) => setCompensationForm((current) => ({ ...current, compensationDate: event.target.value }))} style={inputStyle} />
            </label>
            <label style={fieldStyle}>
              Start
              <input type="time" value={compensationForm.startTime} onChange={(event) => setCompensationForm((current) => ({ ...current, startTime: event.target.value }))} style={inputStyle} />
            </label>
            <label style={fieldStyle}>
              End
              <input type="time" value={compensationForm.endTime} onChange={(event) => setCompensationForm((current) => ({ ...current, endTime: event.target.value }))} style={inputStyle} />
            </label>
          </div>
          <input
            value={compensationForm.note}
            onChange={(event) => setCompensationForm((current) => ({ ...current, note: event.target.value }))}
            placeholder="Optional note"
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: 8 }}
          />
          <button disabled={isCompensating} style={smallButtonStyle} onClick={() => onAddCompensation(leave.id)}>
            Add compensation time
          </button>
        </div>
      )}
      {leave.compensations && leave.compensations.length > 0 && (
        <div>
          <div style={sectionTitleStyle}>Compensation History</div>
          <div style={{ display: 'grid', gap: 6 }}>
            {leave.compensations.map((item) => (
              <div key={item.id} style={{ ...compactRowStyle, fontSize: 13 }}>
                <span>{formatDate(item.compensationDate)} · {item.startTime} - {item.endTime}</span>
                <strong>{item.hours}h</strong>
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <div style={sectionTitleStyle}>Activity Timeline</div>
        <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
          {(leave.activities ?? []).map((activity) => (
            <div key={activity.id} style={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#eef2ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Clock size={14} />
              </div>
              <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 13 }}>{actionLabels[activity.actionType] || activity.actionType}</strong>
                  <span style={{ color: '#64748b', fontSize: 12 }}>{formatDateTime(activity.createdAt)}</span>
                </div>
                <div style={{ color: '#64748b', fontSize: 12, marginTop: 3 }}>
                  {activity.actionBy?.fullName || 'User'} · {activity.actionByRole.toLowerCase()}
                  {activity.oldStatus && activity.newStatus ? ` · ${statusLabel(activity.oldStatus)} to ${statusLabel(activity.newStatus)}` : ''}
                </div>
                {activity.note && <div style={{ marginTop: 6, fontSize: 13 }}>{activity.note}</div>}
              </div>
            </div>
          ))}
          {(leave.activities ?? []).length === 0 && <div style={emptyStyle}>No activity recorded.</div>}
        </div>
      </div>
    </div>
  )
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
      <div style={{ color: '#64748b', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700 }}>{value}</div>
    </div>
  )
}

const panelStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid #dbe4ef',
  borderRadius: 8,
  padding: 16,
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
}

const sectionTitleStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 15,
  fontWeight: 800,
  marginBottom: 12,
}

const fieldStyle: React.CSSProperties = {
  display: 'grid',
  gap: 6,
  fontSize: 12,
  color: '#475569',
  fontWeight: 800,
  textTransform: 'uppercase',
}

const inputStyle: React.CSSProperties = {
  height: 38,
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  padding: '0 10px',
  color: '#0f172a',
  background: '#ffffff',
  fontSize: 14,
  textTransform: 'none',
  fontWeight: 500,
}

const primaryButtonStyle: React.CSSProperties = {
  height: 40,
  border: 'none',
  borderRadius: 6,
  background: '#0f766e',
  color: '#ffffff',
  fontWeight: 800,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  cursor: 'pointer',
}

const confirmCheckStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: 12,
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  background: '#f8fafc',
  color: '#334155',
  fontSize: 13,
  fontWeight: 700,
  lineHeight: 1.4,
}

const smallButtonStyle: React.CSSProperties = {
  height: 30,
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  padding: '0 10px',
  background: '#ffffff',
  color: '#334155',
  fontWeight: 700,
  cursor: 'pointer',
}

const dangerButtonStyle: React.CSSProperties = {
  ...smallButtonStyle,
  color: '#dc2626',
  borderColor: '#fecaca',
  background: '#fff7f7',
}

const summaryCardStyle: React.CSSProperties = {
  minWidth: 88,
  background: '#ffffff',
  border: '1px solid #dbe4ef',
  borderRadius: 8,
  padding: '8px 10px',
  display: 'grid',
  gap: 2,
}

const compactRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  alignItems: 'center',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  padding: 10,
}

const calendarDayStyle: React.CSSProperties = {
  minHeight: 132,
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  background: '#f8fafc',
  padding: 8,
}

const emptyStyle: React.CSSProperties = {
  color: '#64748b',
  fontSize: 13,
  padding: 12,
  background: '#f8fafc',
  border: '1px dashed #cbd5e1',
  borderRadius: 8,
}

const errorStyle: React.CSSProperties = {
  color: '#b91c1c',
  background: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: 6,
  padding: 10,
  fontSize: 13,
}

const thStyle: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 800,
}

const tdStyle: React.CSSProperties = {
  padding: '12px',
  fontSize: 13,
}

